import { useState, useRef, useEffect } from 'react';
import { ArrowLeftRight, ChevronDown, Check, X, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { useDateRange, type ComparisonPreset } from '../context/DateRangeContext';

// ─── Preset config ──────────────────────────────────────────────────────────────

interface PresetOption {
  value: ComparisonPreset;
  label: string;
  shortLabel: string;
}

const PRESETS: readonly PresetOption[] = [
  { value: 'previous_period', label: 'Previous Period', shortLabel: 'Prev Period' },
  { value: 'last_week', label: 'Same Days Last Week', shortLabel: 'Last Week' },
  { value: 'last_month', label: 'Same Period Last Month', shortLabel: 'Last Month' },
  { value: 'last_year', label: 'Same Period Last Year', shortLabel: 'Last Year' },
  { value: 'custom', label: 'Custom Range', shortLabel: 'Custom' },
];

// ─── Component ──────────────────────────────────────────────────────────────────

export function ComparisonToggle() {
  const {
    comparisonPreset,
    setComparisonPreset,
    comparisonRange,
    customComparisonRange,
    setCustomComparisonRange,
  } = useDateRange();

  const [open, setOpen] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [tempStart, setTempStart] = useState('');
  const [tempEnd, setTempEnd] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowCustomInputs(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isActive = comparisonPreset !== 'none';
  const activePreset = PRESETS.find((p) => p.value === comparisonPreset);

  const handlePresetClick = (preset: ComparisonPreset) => {
    if (preset === 'custom') {
      setShowCustomInputs(true);
      return;
    }
    setComparisonPreset(preset);
    setShowCustomInputs(false);
    setOpen(false);
  };

  const handleDisable = () => {
    setComparisonPreset('none');
    setShowCustomInputs(false);
    setOpen(false);
  };

  const applyCustomRange = () => {
    if (!tempStart || !tempEnd) return;
    const start = new Date(tempStart);
    const end = new Date(tempEnd);
    if (start > end) return;
    setCustomComparisonRange({ startDate: start, endDate: end });
    setComparisonPreset('custom');
    setShowCustomInputs(false);
    setOpen(false);
  };

  // Format the comparison range for the chip
  const rangeLabel = comparisonRange
    ? `${format(comparisonRange.startDate, 'MMM d')} – ${format(comparisonRange.endDate, 'MMM d')}`
    : null;

  return (
    <div className="relative w-full" ref={popoverRef}>
      {/* Chip */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'flex w-full h-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium transition-all',
          isActive
            ? 'border border-signalOrange-500/30 bg-signalOrange-500/5 text-signalOrange-600 hover:bg-signalOrange-500/10 dark:border-signalOrange-500/20 dark:bg-signalOrange-500/5 dark:text-signalOrange-400'
            : 'border border-grey-100 bg-white text-charcoalGray-400 hover:bg-grey-50 dark:border-grey-500 dark:bg-grey-800 dark:text-charcoalGray-300 dark:hover:bg-grey-700',
        )}
      >
        <ArrowLeftRight className="size-3.5 shrink-0" />
        <span className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-1 whitespace-normal break-words text-center leading-tight">
          {isActive ? (
            <>
              <span className="hidden sm:inline">vs</span>
              <span className="font-semibold">
                {activePreset?.shortLabel ?? 'Custom'}
              </span>
              {rangeLabel && (
                <span className="hidden text-[10px] font-normal opacity-70 lg:inline">
                  ({rangeLabel})
                </span>
              )}
            </>
          ) : (
            <span>Compare</span>
          )}
        </span>
        <ChevronDown className={cn('size-3 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-grey-100 bg-white shadow-xl dark:border-grey-500 dark:bg-grey-800">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-grey-100 px-4 py-3 dark:border-grey-600">
            <span className="text-xs font-semibold text-charcoalGray-800 dark:text-white">
              Compare With
            </span>
            {isActive && (
              <button
                type="button"
                onClick={handleDisable}
                className="flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium text-charcoalGray-400 transition-colors hover:bg-grey-50 hover:text-charcoalGray-700 dark:hover:bg-grey-700 dark:hover:text-white"
              >
                <X className="size-3" />
                Disable
              </button>
            )}
          </div>

          {/* Quick presets */}
          <div className="p-2">
            {PRESETS.map((preset) => {
              const isSelected = comparisonPreset === preset.value;
              return (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => handlePresetClick(preset.value)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs transition-all',
                    isSelected
                      ? 'bg-signalOrange-500/10 text-signalOrange-600 dark:text-signalOrange-400'
                      : 'text-charcoalGray-600 hover:bg-grey-50 dark:text-charcoalGray-200 dark:hover:bg-grey-700',
                  )}
                >
                  <span className="font-medium">{preset.label}</span>
                  {isSelected && <Check className="size-3.5 text-signalOrange-500" />}
                </button>
              );
            })}
          </div>

          {/* Custom date inputs */}
          {showCustomInputs && (
            <div className="border-t border-grey-100 p-3 dark:border-grey-600">
              <div className="mb-3 flex items-center gap-2">
                <Calendar className="size-3.5 text-charcoalGray-400" />
                <span className="text-[11px] font-semibold text-charcoalGray-500 dark:text-charcoalGray-300">
                  Custom Range
                </span>
              </div>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={tempStart}
                  onChange={(e) => setTempStart(e.target.value)}
                  className="flex-1 rounded-lg border border-grey-100 bg-white px-2.5 py-2 text-xs text-charcoalGray-800 outline-none focus:border-signalOrange-500 dark:border-grey-500 dark:bg-grey-700 dark:text-white"
                />
                <span className="flex items-center text-[10px] text-charcoalGray-300">to</span>
                <input
                  type="date"
                  value={tempEnd}
                  onChange={(e) => setTempEnd(e.target.value)}
                  className="flex-1 rounded-lg border border-grey-100 bg-white px-2.5 py-2 text-xs text-charcoalGray-800 outline-none focus:border-signalOrange-500 dark:border-grey-500 dark:bg-grey-700 dark:text-white"
                />
              </div>
              <button
                type="button"
                onClick={applyCustomRange}
                disabled={!tempStart || !tempEnd}
                className="mt-2 w-full rounded-lg bg-signalOrange-500 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-signalOrange-600 disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          )}

          {/* Current range footer */}
          {isActive && rangeLabel && (
            <div className="border-t border-grey-100 px-4 py-2.5 dark:border-grey-600">
              <div className="flex items-center gap-2 text-[10px]">
                <span className="text-charcoalGray-400 dark:text-charcoalGray-300">Comparing with:</span>
                <span className="font-semibold text-signalOrange-600 dark:text-signalOrange-400">
                  {rangeLabel}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
